import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import AppInput from '../atoms/AppInput';
import AppBadge from '../atoms/AppBadge';
import InfoHint from '../atoms/InfoHint';
import { IconDollarSign } from '../atoms/Icon';

/**
 * Os cinco tipos de chave, com o que cada um espera no campo.
 *
 * O tipo é escolhido pelo lojista, e não deduzido do que ele digitou: onze
 * dígitos são um CPF ou um telefone sem DDI, e é o cliente que paga o preço do
 * palpite errado — ele procura no campo errado do aplicativo do banco e desiste.
 */
export const TIPOS_PIX = [
  { id: 'cpf', label: 'CPF', placeholder: '000.000.000-00', modo: 'numeric', digitos: [11, 11] },
  { id: 'cnpj', label: 'CNPJ', placeholder: '00.000.000/0000-00', modo: 'numeric', digitos: [14, 14] },
  { id: 'phone', label: 'Telefone', placeholder: '(00) 00000-0000', modo: 'tel', digitos: [10, 13] },
  { id: 'email', label: 'E-mail', placeholder: 'loja@exemplo.com.br', modo: 'email' },
  { id: 'random', label: 'Aleatória', placeholder: '00000000-0000-0000-0000-000000000000', modo: 'text' },
];

const TIPO_PADRAO = 'cnpj';

const digitos = (v) => (v || '').replace(/\D/g, '');

/** Estado do formulário a partir do que veio da API. */
export function buildPix(establishment) {
  return {
    // Sem chave cadastrada a tela abre em CNPJ, o caso mais comum de conta de
    // estabelecimento. Abrir sem tipo nenhum obrigaria a uma escolha antes de
    // ver o campo, e o campo é o que explica a escolha.
    keyType: establishment?.pixKeyType || TIPO_PADRAO,
    key: establishment?.pixKey || '',
    holderName: establishment?.pixHolderName || '',
  };
}

export function samePix(a, b) {
  // Chave vazia é o mesmo cadastro qualquer que seja o tipo escolhido: mudar o
  // seletor sem digitar nada não deve acender a barra de "alterações não salvas".
  if (!a.key.trim() && !b.key.trim()) return true;
  return (
    a.keyType === b.keyType &&
    a.key.trim() === b.key.trim() &&
    a.holderName.trim() === b.holderName.trim()
  );
}

/** O que impede de salvar, ou nulo. As contagens repetem as do backend de
 *  propósito: dizer "faltam dígitos" ao lado do campo evita uma ida ao servidor
 *  para receber a mesma frase num alerta no topo da página. */
export function pixError(pix) {
  const chave = pix.key.trim();
  if (!chave) return null;

  const tipo = TIPOS_PIX.find((t) => t.id === pix.keyType);
  if (!tipo) return 'Escolha o tipo da chave Pix.';

  if (tipo.digitos) {
    const [minimo, maximo] = tipo.digitos;
    const n = digitos(chave).length;
    if (n < minimo || n > maximo) {
      const esperado = minimo === maximo ? `${minimo}` : `${minimo} a ${maximo}`;
      return `A chave Pix de ${tipo.label} deve ter ${esperado} dígitos.`;
    }
  }
  if (tipo.id === 'email' && !/^\S+@\S+\.\S+$/.test(chave)) {
    return 'A chave Pix de e-mail parece inválida.';
  }
  if (tipo.id === 'random' && chave.length !== 36) {
    return 'A chave Pix aleatória tem 36 caracteres.';
  }
  return null;
}

/** Converte o estado da tela para o corpo do PATCH.
 *
 *  Chave em branco vai como string vazia, e não omitida: é assim que o backend
 *  entende "apague o cadastro" — omitir significa "não mandei", e o lojista que
 *  limpou o campo veria a chave antiga voltar no próximo carregamento. */
export function pixToPayload(pix) {
  const chave = pix.key.trim();
  if (!chave) return { pixKey: '' };
  return {
    pixKey: chave,
    pixKeyType: pix.keyType,
    pixHolderName: pix.holderName.trim(),
  };
}

function PixPaymentForm({ value, onChange, nomeDoEstabelecimento }) {
  const tipo = TIPOS_PIX.find((t) => t.id === value.keyType) || TIPOS_PIX[0];
  const erro = pixError(value);
  const temChave = !!value.key.trim();

  const mudar = (campos) => onChange({ ...value, ...campos });

  return (
    // Destacado dos outros cartões da tela: sem a chave cadastrada, o
    // estabelecimento não recebe — é a configuração que mais custa deixar
    // passar batido, então a borda e o fundo âmbar chamam mais atenção que os
    // cartões vizinhos, todos no mesmo `card-elevated` neutro.
    <Card
      className="border-0 card-elevated"
      style={{
        borderRadius: 'var(--radius-md)',
        borderLeft: '4px solid var(--color-amber-primary)',
        background: 'linear-gradient(120deg, var(--color-amber-ghost) 0%, #fff 65%)',
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
          <IconDollarSign size={16} style={{ color: 'var(--color-amber-primary)' }} />
          <h6 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>
            Recebimento por Pix
          </h6>
          {!temChave && <AppBadge variant="amber">Configure para receber pagamentos</AppBadge>}
        </div>
        <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
          Vai na mensagem que o cliente envia com o comprovante, pronta para copiar.
        </p>

        <div className="form-row-split">
          <div style={{ flex: '0 1 180px' }}>
            <Form.Group className="mb-3">
              <Form.Label>Tipo da chave</Form.Label>
              <Form.Select
                value={value.keyType}
                onChange={(e) => mudar({ keyType: e.target.value })}
              >
                {TIPOS_PIX.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="flex-grow-1">
            <AppInput
              label="Chave Pix"
              value={value.key}
              onChange={(e) => mudar({ key: e.target.value })}
              placeholder={tipo.placeholder}
              inputMode={tipo.modo === 'text' ? undefined : tipo.modo}
              type={tipo.modo === 'email' ? 'email' : 'text'}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              maxLength={77}
              error={erro || undefined}
              hint="Deixe em branco se não recebe por Pix."
            />
          </div>
        </div>

        {/* Só depois da chave: sem ela o campo não tem o que qualificar, e um
            beneficiário sozinho não vai a lugar nenhum. */}
        {temChave && (
          <>
            <AppInput
              label="Nome do beneficiário"
              value={value.holderName}
              onChange={(e) => mudar({ holderName: e.target.value })}
              placeholder={nomeDoEstabelecimento || 'Razão social ou nome do titular'}
              autoComplete="off"
              autoCapitalize="words"
              maxLength={255}
            />
            <InfoHint>
              É o nome que o cliente vê no aplicativo do banco antes de confirmar. Se for
              diferente do nome do cardápio, escreva-o aqui — divergência entre os dois é o
              que faz a transferência ser abandonada. Em branco, usamos o nome do
              estabelecimento.
            </InfoHint>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default PixPaymentForm;
